/**
 * Analytics utilities with dynamic import to avoid bloating main bundle.
 * Follows the pattern: lazy-load Firebase Analytics only when needed.
 */

import type { Analytics } from "firebase/analytics";

type AnalyticsEvent = string;
type AnalyticsParams = Record<string, unknown>;

let analyticsInstance: Analytics | null = null;

/**
 * Lazy-load Firebase Analytics.
 */
async function getAnalytics(): Promise<Analytics | null> {
  if (analyticsInstance) return analyticsInstance;
  
  try {
  const { getAnalytics: initAnalytics } = await import("firebase/analytics");
    const { app } = await import("@/firebase");
    analyticsInstance = initAnalytics(app);
    return analyticsInstance;
  } catch (error) {
    console.warn("[Analytics] Failed to initialize:", error);
    return null;
  }
}

/**
 * Log an analytics event.
 * @param eventName - Name of the event
 * @param params - Event parameters
 */
export async function logEvent(eventName: AnalyticsEvent, params?: AnalyticsParams): Promise<void> {
  try {
    const analytics = await getAnalytics();
    if (!analytics) return;
    
    const { logEvent: firebaseLogEvent } = await import("firebase/analytics");
    firebaseLogEvent(analytics, eventName, params);
  } catch (error) {
    console.warn("[Analytics] Failed to log event:", eventName, error);
  }
}

/**
 * Set user properties.
 * @param properties - User properties to set
 */
export async function setUserProperties(properties: AnalyticsParams): Promise<void> {
  try {
    const analytics = await getAnalytics();
    if (!analytics) return;
    
    const { setUserProperties: firebaseSetUserProperties } = await import("firebase/analytics");
    firebaseSetUserProperties(analytics, properties);
  } catch (error) {
    console.warn("[Analytics] Failed to set user properties:", error);
  }
}
